/* Local persistence for the Create Notes template tab order.

   /api/favorite has no ordering column — Prisma returns favourites by composite
   primary key — so the order a clinician drags their tabs into cannot be saved
   server-side without a backend change, and this repo is frontend-only. Storing
   it here means the order survives a reload on the machine it was set on, which
   is the case that actually matters; it does not follow the user to another
   device. Raised for the backend.

   Every read is defensive: storage throws in private mode on some browsers, and
   the stored value is user-editable. A bad value must degrade to "no saved
   order", never to a crash on the app's primary screen. */

const STORAGE_KEY = "nirvascribe_template_order";

const readOrder = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => Number.isFinite(id)) : [];
  } catch (_) {
    return [];
  }
};

export const saveTemplateOrder = (templateIds) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(templateIds));
  } catch (_) {
    // A saved order is a convenience. Losing it is not worth an error path.
  }
};

/**
 * Re-sequence today's template ids to the last order the user dragged them into.
 * Ids with no saved position keep the incoming order and go last, so a template
 * favourited on another device still appears rather than being dropped.
 *
 * @param {number[]} templateIds ids in their server order
 * @returns {number[]} a new array; the input is never mutated
 */
export const applySavedTemplateOrder = (templateIds) => {
  const saved = readOrder();
  if (!saved.length) return templateIds;

  const known = templateIds.filter((id) => saved.includes(id));
  const unknown = templateIds.filter((id) => !saved.includes(id));

  return [...known.sort((a, b) => saved.indexOf(a) - saved.indexOf(b)), ...unknown];
};
